import { Button, FeatureFlag, Http } from "@bankmonitor/bm-ui-kit";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import * as React from "react";
import { Route } from "react-router-dom";
import Notifications from "@component/Notifications";
import LoginPage from "@page/auth/LoginPage";
import LostPasswordPage from "@page/auth/LostPasswordPage";
import RegistrationPage from "@page/auth/RegistrationPage";
import LoadingPage from "@page/LoadingPage";
import MaintenancePage from "@page/MaintenancePage";
import NotFoundPage from "@page/NotFoundPage";
import type { AuthenticationContextType, MFAStatus } from "@service/AuthenticationContext";
import AuthContext from "@service/AuthenticationContext";
import AuthenticationService from "@service/AuthenticationService";
import type { UserData } from "@service/AuthenticationService.type";
import { BM_API_URL, BM_VERSION, MAINTENANCE_ENABLED } from "@util/Config";
import { reportAuthState } from "@util/measurement/pageInit";
import { SentryRoutes } from "@util/Sentry";
import routes, { ANALYSIS_ROUTES } from "./routes";
import AccountAnalysisService from "@service/analysis/AccountAnalysisService";

const CHECK_VERSION_INTERVALL = 60_000;

type Props = {};

interface State {
	userData: UserData;
	loading: boolean;
	mfaStatus: MFAStatus;
	accountAnalysisEnabled: boolean | null;
}

export default class App extends React.Component<Props, State> {
	private timerUpdateChecker: NodeJS.Timeout;

	public constructor(props: Props) {
		super(props);

		this.state = { userData: undefined, loading: true, mfaStatus: "none", accountAnalysisEnabled: null };
	}

	public async componentDidMount(): Promise<void> {
		await FeatureFlag.init(`${BM_API_URL}/api/public/config/feature/flags`);
		// CSRF token
		AuthenticationService.init().then(this.storedTokenValidator).then(this.updateChecker);

		document.addEventListener("visibilitychange", this.handleVisibilityChange, false);
	}

	public componentWillUnmount(): void {
		document.removeEventListener("visibilitychange", this.handleVisibilityChange, false);

		clearInterval(this.timerUpdateChecker);
	}

	public componentDidUpdate(_prevProps: Props, prevState: State): void {
		const wasAuthenticated = this.isAuthenticated(prevState);
		const nowAuthenticated = this.isAuthenticated(this.state);

		if (nowAuthenticated && !wasAuthenticated) {
			this.refreshAccountAnalysis();
		} else if (!nowAuthenticated && wasAuthenticated) {
			AccountAnalysisService.reset();
			this.setState({ accountAnalysisEnabled: null });
		}

		// page_init is tied to the auth state and also waits for MFA: the user fields only go
		// out once the app itself considers the user signed in.
		if (!this.state.loading) {
			reportAuthState(this.state.userData, nowAuthenticated);
		}
	}

	private isAuthenticated(state: State): boolean {
		return !!state.userData && state.mfaStatus === "success";
	}

	private refreshAccountAnalysis(): void {
		AccountAnalysisService.init().then(() => {
			// Discard the result if the user logged out while the fetch was in flight.
			if (this.isAuthenticated(this.state)) {
				this.setState({ accountAnalysisEnabled: AccountAnalysisService.isEnabled() });
			} else {
				AccountAnalysisService.reset();
			}
		});
	}

	@autobind
	private handleVisibilityChange() {
		if (document.visibilityState === "hidden") {
			clearInterval(this.timerUpdateChecker);
		} else {
			this.storedTokenValidator().then(this.updateChecker);
		}
	}

	private getAuthContextValue(): AuthenticationContextType {
		const { userData, mfaStatus } = this.state;

		return {
			userData,
			mfaStatus,
			setUser: this.updateUserData,
			// biome-ignore lint/style/useNamingConvention: <explanation>
			setMFAStatus: this.updateSMSSent,
		};
	}

	@autobind
	private storedTokenValidator(): Promise<void> {
		return AuthenticationService.getUserData()
			.then((userData: UserData) => {
				this.setState({
					userData,
					mfaStatus: AuthenticationService.hasMFA() ? "success" : "none",
					loading: false,
				});
			})
			.catch(() => {
				AuthenticationService.resetMFA();

				this.setState({
					userData: undefined,
					mfaStatus: "none",
					loading: false,
				});
			});
	}

	@autobind
	private updateChecker(): void {
		clearInterval(this.timerUpdateChecker);

		this.timerUpdateChecker = setInterval(() => {
			Http.get(`/VERSION?z=${Date.now()}`)
				.then((response) => response.text())
				.then((version: string) => {
					if (version.split(".").pop().trim() !== BM_VERSION.trim()) {
						Notifications.pushInfoMessage(
							<>
								Töltsd újra az oldalt, hogy Te is elérd a legújabb funkciókat!
								<div className="mt-2 text-end">
									<Button size="sm" outlined onClick={() => window.location.reload()}>
										Újratöltés
									</Button>
								</div>
							</>,
							"Új verzió érhető el!",
						);

						window.BM_NEED_RELOAD = true;

						clearInterval(this.timerUpdateChecker);
					}
				})
				.catch(() => {
					clearInterval(this.timerUpdateChecker);
				});
		}, CHECK_VERSION_INTERVALL);
	}

	@autobind
	private updateUserData(userData: UserData): void {
		this.setState({
			userData,
		});
	}

	@autobind
	private updateSMSSent(mfaStatus: MFAStatus): void {
		this.setState({
			mfaStatus,
		});
	}

	public render(): ReactNode {
		const { userData, mfaStatus, loading, accountAnalysisEnabled } = this.state;

		if (MAINTENANCE_ENABLED) {
			return <MaintenancePage />;
		}

		if (loading) {
			return <LoadingPage />;
		}

		if (!userData || mfaStatus !== "success") {
			return (
				<AuthContext.Provider value={this.getAuthContextValue()}>
					<SentryRoutes>
						<Route path="/regisztracio/" element={<RegistrationPage />} />
						<Route path="/elfelejtett-jelszo/" element={<LostPasswordPage />} />
						<Route path="*" element={<LoginPage />} />
					</SentryRoutes>
				</AuthContext.Provider>
			);
		}

		// Authenticated, but the analysis availability check has not resolved yet — keep showing the
		// loader instead of rendering routes, otherwise an analysis URL would briefly hit NotFoundPage.
		if (accountAnalysisEnabled === null) {
			return <LoadingPage />;
		}

		// biome-ignore lint/style/useNamingConvention: <explanation>
		const ROUTES = accountAnalysisEnabled ? [...routes, ...ANALYSIS_ROUTES] : routes;

		return (
			<AuthContext.Provider value={this.getAuthContextValue()}>
				<main>
					<div className="main-content">
						<SentryRoutes>
							{ROUTES.map(({ path, element: Component }) => (
								<Route
									key={path}
									path={path}
									element={
										<React.Suspense fallback={<LoadingPage />}>
											<Component />
										</React.Suspense>
									}
								/>
							))}
							<Route path="*" element={<NotFoundPage />} />
						</SentryRoutes>
					</div>
				</main>
			</AuthContext.Provider>
		);
	}
}
